import type { AccountSecurityState, AccountSecurityViewModel } from "@bundle/core";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { EmailCodeStep, signInErrorText } from "./SignInForm.tsx";
import { SecuritySheet } from "./settings/AccountSecuritySettings.tsx";
import { PhoneNumberSteps } from "./PhoneSteps.tsx";
import { Banner, Button, Divider, Icon, Pressable, Spinner, Stack, Text, TextField } from "../primitives/index.ts";
import { useT } from "../i18n/I18nProvider.tsx";
import { useSession } from "../hooks/index.ts";

export type SignupFormProps = {
  /** Where an Apple / Google redirect comes back to. */
  returnTo: string;
  /** "Sign in instead" (the taken address) or the footer's "Sign in": the page goes to sign-in. */
  onSignIn: () => void;
};

/**
 * Create account (design "6 · Create account"): A Apple / Google, or full name + work email → Continue, which checks
 * the address first (SessionViewModel.startSignup). D the address already has an account: a notice with "Sign in
 * instead", no code sent. B the email code, the same step as sign-in (the name goes with it). C is SecureAccountStep.
 */
export function SignupForm({ returnTo, onSignIn }: SignupFormProps) {
  const t = useT();
  const { vm, state } = useSession();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  useEffect(() => {
    void vm.loadMethods();
  }, [vm]);

  const busy = state.busy;
  const error =
    state.error === undefined ? null : (
      <Stack direction="horizontal" gap="2" align="start" testID="signup-error">
        <Icon name="alert" size="icon.sm" color="status.danger" />
        <Text variant="footnote" color="status.danger">
          {signInErrorText(t, state.error)}
        </Text>
      </Stack>
    );
  const signup = state.signup;

  if (signup?.step === "code" && state.emailStep === "code" && state.codeEmail !== undefined) {
    return <EmailCodeStep email={state.codeEmail} busy={busy} error={error} onChangeEmail={() => vm.signupDifferentEmail()} signup />;
  }

  const methods = state.methods;
  const exists = signup?.step === "exists" ? signup.email : undefined;
  const submit = () => void vm.startSignup(name, email);
  const apple = methods?.apple === true && vm.canSignInWithApple;
  const google = methods?.google === true;
  const emailCode = methods === undefined || methods.emailCode;
  return (
    <Stack gap="4" testID={exists === undefined ? "signup-details" : "signup-exists"}>
      {exists === undefined && (apple || google) ? (
        <>
          <Stack gap="3">
            {apple ? <Button label={t("auth.apple")} brand="apple" size="lg" loading={busy === "apple"} onPress={() => void vm.signInWithApple(returnTo)} testID="signup-apple" /> : null}
            {google ? <Button label={t("auth.google")} brand="google" size="lg" loading={busy === "google"} onPress={() => void vm.signInWithGoogle(returnTo)} testID="signup-google" /> : null}
          </Stack>
          {emailCode ? (
            <Stack direction="horizontal" gap="3" align="center">
              <Stack grow>
                <Divider />
              </Stack>
              <Text variant="footnote" color="text.tertiary">
                {t("auth.or")}
              </Text>
              <Stack grow>
                <Divider />
              </Stack>
            </Stack>
          ) : null}
        </>
      ) : null}
      {emailCode ? (
        <Stack gap="3">
          <TextField label={t("auth.signup.name")} value={name} onChange={setName} contentType="name" autoFocus={!apple && !google} disabled={exists !== undefined} onSubmit={submit} testID="signup-name" />
          <TextField
            label={t("auth.signup.email")}
            placeholder={t("auth.emailPlaceholder")}
            value={exists ?? email}
            onChange={setEmail}
            keyboard="email"
            disabled={exists !== undefined}
            onSubmit={submit}
            testID="signup-email"
          />
          {exists === undefined ? (
            <>
              {error}
              <Button label={t("auth.continue")} variant="primary" size="lg" loading={busy === "checking" || busy === "sendingCode"} disabled={email.trim() === ""} onPress={submit} testID="signup-continue" />
            </>
          ) : (
            <>
              <Banner
                tone="warning"
                title={t("auth.signup.existsTitle")}
                message={t("auth.signup.existsBody", { email: exists })}
                action={{ label: t("auth.signup.signInInstead"), onPress: onSignIn, variant: "primary", testID: "signup-sign-in-instead" }}
                testID="signup-exists-notice"
              />
              <Stack direction="horizontal" justify="center">
                <Button label={t("auth.code.changeEmail")} variant="ghost" size="sm" onPress={() => vm.signupDifferentEmail()} testID="signup-other-email" />
              </Stack>
            </>
          )}
        </Stack>
      ) : (
        error
      )}
    </Stack>
  );
}

export type SecureAccountStepProps = {
  /** Account & sign-in (the same passkey registration and set-password call as Settings). */
  security: { vm: AccountSecurityViewModel; state: AccountSecurityState };
  /** This browser can make passkeys. */
  passkeys: boolean;
  /** A passkey or password was added, or "Skip for now": on to the workspace flow. */
  onDone: () => void;
  onCopy: (text: string) => void;
};

/** Create account, C: "Secure your account" (optional): add a passkey (recommended), set a password, or skip. */
export function SecureAccountStep({ security, passkeys, onDone, onCopy }: SecureAccountStepProps) {
  const t = useT();
  const { vm, state: s } = security;
  // Saved (a passkey or a password) → done; Saved counts up on every change that went through.
  const savedAt = useRef(s.saved.n);
  useEffect(() => {
    if (s.saved.n > savedAt.current && s.sheet === undefined) onDone();
  }, [s.saved.n, s.sheet, onDone]);
  const addPasskey = () => {
    vm.startAddPasskey("");
    void vm.savePasskey();
  };
  const error = s.error !== undefined && s.sheet?.kind === "passkeyName" ? s.error : undefined;
  return (
    <Stack gap="6" testID="signup-secure">
      <Stack gap="1">
        <Text variant="title2" weight="bold">
          {t("auth.signup.secureTitle")}
        </Text>
        <Text color="text.secondary">{t("auth.signup.secureSubtitle")}</Text>
      </Stack>
      <Stack gap="3">
        {passkeys ? (
          <Option
            icon="key"
            title={t("auth.signup.addPasskey")}
            badge={t("auth.signup.recommended")}
            body={t("auth.signup.passkeyBody")}
            busy={s.busy && s.sheet?.kind === "passkeyName"}
            highlighted
            onPress={addPasskey}
            testID="signup-add-passkey"
          />
        ) : null}
        <Option icon="lock" title={t("auth.signup.setPassword")} body={t("auth.signup.passwordBody")} highlighted={!passkeys} onPress={() => vm.startPassword("set")} testID="signup-set-password" />
        {error !== undefined ? <PasskeyError vm={vm} /> : null}
      </Stack>
      <Stack direction="horizontal" justify="center">
        <Button label={t("auth.signup.skip")} variant="ghost" onPress={onDone} testID="signup-skip" />
      </Stack>
      {s.sheet?.kind === "password" ? <SecuritySheet vm={vm} state={s} onCopy={onCopy} /> : null}
    </Stack>
  );
}

export type VerifyPhoneStepProps = {
  security: { vm: AccountSecurityViewModel; state: AccountSecurityState };
  /** Verified, or "Skip for now" (not offered when the email is hosted on Bundle): on to "Secure your account". */
  onDone: () => void;
  /** The platform can read the number from Google. */
  google: boolean;
  /** Back from Google with the phone scope: read the number now. */
  importNow?: boolean;
};

/**
 * Create account, after the code (design "Phone number sign-in" A–C): "Verify your phone" (strongly recommended), or
 * "Add a phone number" without Skip when the email is hosted on Bundle. Pre-filled from Google or Slack when known.
 */
export function VerifyPhoneStep({ security, onDone, google, importNow = false }: VerifyPhoneStepProps) {
  const t = useT();
  const { vm, state: s } = security;
  const open = s.sheet?.kind === "phone";
  useEffect(() => {
    if (!open && s.sheet === undefined && s.phone === undefined) vm.startPhone();
  }, [open, s.sheet, s.phone, vm]);
  const imported = useRef(false);
  useEffect(() => {
    if (importNow && open && !imported.current) {
      imported.current = true;
      void vm.importGooglePhone();
    }
  }, [importNow, open, vm]);
  useEffect(() => {
    if (s.phone !== undefined) onDone();
  }, [s.phone, onDone]);
  const code = s.sheet?.kind === "phone" && s.sheet.step === "code";
  return (
    <Stack gap="6" testID="signup-phone">
      <Stack gap="1">
        <Text variant="title2" weight="bold">
          {code ? t("phone.codeTitle") : s.phoneRequired ? t("phone.requiredTitle") : t("phone.verifyTitle")}
        </Text>
        {code ? null : <Text color="text.secondary">{s.phoneRequired ? t("phone.requiredBody") : t("phone.verifyBody")}</Text>}
      </Stack>
      <PhoneNumberSteps vm={vm} state={s} google={google} {...(s.phoneRequired ? {} : { onSkip: onDone })} />
    </Stack>
  );
}

function PasskeyError({ vm }: { vm: AccountSecurityViewModel }): ReactNode {
  const t = useT();
  return (
    <Stack direction="horizontal" gap="2" align="start" testID="signup-passkey-error">
      <Icon name="alert" size="icon.sm" color="status.danger" />
      <Stack grow gap="1">
        <Text variant="footnote" color="status.danger">
          {t("auth.error.invalid")}
        </Text>
      </Stack>
      <Button label={t("common.dismiss")} variant="ghost" size="sm" onPress={() => vm.close()} testID="signup-passkey-error-dismiss" />
    </Stack>
  );
}

function Option(p: { icon: "key" | "lock"; title: string; body: string; badge?: string; highlighted: boolean; busy?: boolean; onPress: () => void; testID: string }) {
  return (
    <Pressable onPress={p.onPress} disabled={p.busy === true} accessibilityLabel={p.title} radius="lg" testID={p.testID}>
      <Stack direction="horizontal" gap="3" align="center" padding="4" radius="lg" border={p.highlighted ? "accent.default" : "border.strong"} background={p.highlighted ? "accent.subtle" : undefined}>
        <Icon name={p.icon} size="icon.md" color="text.primary" />
        <Stack grow gap="0.5">
          <Stack direction="horizontal" gap="2" align="center">
            <Text weight="semibold">{p.title}</Text>
            {p.badge !== undefined ? (
              <Text variant="caption" weight="semibold" color="accent.text">
                {p.badge}
              </Text>
            ) : null}
          </Stack>
          <Text variant="footnote" color="text.secondary">
            {p.body}
          </Text>
        </Stack>
        {p.busy === true ? <Spinner size="icon.sm" /> : <Icon name="chevronRight" size="icon.sm" color="text.tertiary" />}
      </Stack>
    </Pressable>
  );
}
